import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { fullVideoEtaSec } from 'fym-shared/src/music24/fullVideoPlan';
import { isFullVideoTheme, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { clockTime, fullVideoLengthOf, PickLine } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
import { studioMessages } from '../studioMessages';
import type { StudioDetails } from '../studioRules';

export interface RailExtras {
  /** More "label · value" rows of the rail summary. */
  rows: Array<[string, string]>;
  /** The wait line under "Make it" (design s7, s10); null for an asset type with no line. */
  wait: string | null;
}

interface UseRailExtrasProps {
  themeId: StudioThemeId;
  details: StudioDetails;
  durationSec: number | null;
  /** The lines of a lyric moment, for the "Lines 0:58 – 1:13" row. */
  lines: readonly PickLine[];
}

/**
 * The rail rows and the wait line of the full videos, the lyric moment and the motion artwork
 * (designs s7, s7b, s10): the length and the file of a full video and "Takes about 15 minutes for
 * 3:54" from `fullVideoEtaSec`; the lines and "About 2 minutes." of a lyric moment; the files of
 * the motion artwork.
 */
export function useRailExtras({ themeId, details, durationSec, lines }: UseRailExtrasProps): RailExtras {
  const intl = useIntl();
  return useMemo<RailExtras>(() => {
    if (isFullVideoTheme(themeId)) {
      const lengthSec = fullVideoLengthOf(details.trim, durationSec);
      const rows: Array<[string, string]> = [
        [intl.formatMessage(fullVideoMessages.railFile), intl.formatMessage(fullVideoMessages.railFileFull)],
      ];
      if (lengthSec === null) {
        return { rows, wait: null };
      }
      rows.unshift([intl.formatMessage(studioMessages.lengthTitle), clockTime(lengthSec)]);
      const etaSec = fullVideoEtaSec(themeId, lengthSec);
      return {
        rows,
        wait:
          etaSec === null
            ? null
            : intl.formatMessage(fullVideoMessages.waitFull, {
                minutes: Math.round(etaSec / 60),
                length: clockTime(lengthSec),
              }),
      };
    }
    if (themeId === 'lyricMoment') {
      const first = lines.find((line) => line.index === details.lineFrom)?.startMs ?? null;
      const last = lines.find((line) => line.index === details.lineTo);
      const lastMs = last?.endMs ?? last?.startMs ?? null;
      const rows: Array<[string, string]> =
        first === null || lastMs === null
          ? []
          : [
              [
                intl.formatMessage(fullVideoMessages.linesTitle),
                `${clockTime(first / 1000)} – ${clockTime(lastMs / 1000)}`,
              ],
            ];
      return { rows, wait: intl.formatMessage(fullVideoMessages.waitMoment) };
    }
    if (themeId === 'motionArtwork') {
      return {
        rows: [[intl.formatMessage(fullVideoMessages.railFile), intl.formatMessage(fullVideoMessages.railFileMotion)]],
        wait: null,
      };
    }
    return { rows: [], wait: null };
  }, [details.lineFrom, details.lineTo, details.trim, durationSec, intl, lines, themeId]);
}
